import type { TextMeasurer } from "@tento-lona/sheets-ui/sheet/default-legend-layout.ts";
import { AllDayEventLegendLayout } from "./timeline-row-event/all-day-events-plugin.legend-layout.ts";
import { Tracing } from "@tento-core/tracing";
import { ColumnIndex } from "@tento-ui/components/sheet/column-index";
import { TimelineColumnElement } from "@tento-lona/sheets-ui/plugins/layout/timeline-shared/timeline-column.ts";
import type { Option, RowLocalId, RowNetworkKey } from "@tento-lona/sheets";
import { EventId, RowId, EventItemAllDay } from "@tento-lona/sheets";
import { CalendarAllDayEventsProvider, ColumnTimelineRow } from "@tento-lona/sheets/platform";
import { SheetDelegate } from "@tento-lona/sheets-ui/sheet/sheet-delegate.ts";
import {
  type BindCellContext,
  type FixedChildRowContext,
  type RenderPlugin,
  RowOptionsElement,
  type SheetLegendClass,
} from "@tento-lona/sheets-ui/sheet/render-plugin.ts";
import { type SheetRow, CalendarsClient, type EventItemPresentation } from "@tento-lona";
import {
  TimelineLayoutManager,
  TimelineLayoutManagerDelegate,
} from "@tento-ui/components/timeline/timeline-layout-manager";
import { Rational } from "@tento-core/rational";
import { ListNestedIcon, LonaIcons } from "@tento-ui/ui/icons";
import { DaysSinceEpoch, NaiveDate, TimezoneRegion } from "@tento-chrono";
import { AllDayEventsCell } from "./timeline-row-event/all-day-events/cell.ts";
import { AllDayEventLegend } from "./timeline-row-event/all-day-events/legend.ts";
import { MoreIndicator } from "./timeline-row-event/all-day-events/more-indicator.ts";
import { EventTooltip } from "./column-calendar/components/event-tooltip.ts";
import {
  AllDayEventGhost,
  GHOST_ID,
} from "@tento-lona/sheets-ui/sheet/gestures/all-day-event-ghost.ts";
import { AllDayEventInfo } from "@tento-lona/sheets-ui/sheet/gestures/all-day-event-info.ts";
import { EventAccess } from "@tento-lona/sheets-ui/sheet/gestures/event-access.ts";
import type { GestureCoordinatorLike } from "@tento-lona/sheets-ui/sheet/gestures/gesture-system.ts";
import type { AllDayActionsLike } from "@tento-lona/sheets-ui/sheet/gestures/calendar-actions-like.ts";
import { Lazy } from "@tento-core/lazy";
import type { TimelineContentProvider } from "@tento-lona/sheets-ui/plugins/layout/timeline-shared/timeline-content-provider.ts";
import { findTimelineProviderForRendererRow } from "../shared/calendar-subcolumn/provider-discovery.ts";
import { TIMELINE_ROW_EVENT_SPEC } from "@tento-lona/sheets/platform";
import type { PerTypeSpec } from "@tento-lona/sheets/platform";
import type { EventActiveState } from "./event/event-active-state.ts";
import { optionLiveData } from "../../sheet/option-live-data.ts";

/** The renderer plugin that lays all-day events along a timeline row. */
export class TimelineRowEventRendererPlugin
  implements
    RenderPlugin<TimelineColumnElement<AllDayEventInfo>, AllDayEventLegend>,
    TimelineLayoutManagerDelegate<
      EventItemAllDay,
      AllDayEventGhost,
      AllDayEventInfo
    >
{
  static readonly spec: PerTypeSpec = TIMELINE_ROW_EVENT_SPEC;

  readonly role = "renderer";
  readonly id = "timeline[row].event";
  readonly icon: HTMLTemplateElement = LonaIcons.$template(ListNestedIcon);

  /**
   * Pure data half — exposed so CLI inspection and the shared catalog
   * `dataProvider` factory get the same row-walking + full-day filter
   * logic that DOM uses for fixed-child output.
   */
  readonly dataProvider: CalendarAllDayEventsProvider =
    new CalendarAllDayEventsProvider();

  config = {
    cellHeightPx: 22,
    defaultNumSubrows: 1,
    marginEndPx: 12,
    maxRows: 8,
  };

  #today = NaiveDate.today();

  // One manager per row - managers have no concept of rowId
  readonly #managers = new Map<
    RowLocalId, // rowId (interned RowLocalId — reference equality via Map)
    TimelineLayoutManager<EventItemAllDay, AllDayEventGhost, AllDayEventInfo>
  >();

  // Per-row handle storage for accessing fixed children outside render cycle
  readonly #handlesByRowId = new Map<RowLocalId, SheetDelegate>();

  /** Last `header/allday-fixed-children` payload per row, so the breadcrumb
   *  records CHANGES to the strip's content census instead of re-logging the
   *  same numbers on every rendered-columns emit. */
  readonly #lastAllDayBreadcrumb = new Map<RowLocalId, string>();

  #ghostRowId: Option<RowLocalId> = null;
  #$ghost: Option<AllDayEventsCell> = null;
  // Track when ghost pointer events should be disabled (during active drag)
  #ghostPointerEventsDisabled: boolean = false;

  /**
   * In-flight existing-event drag. Instead of a separate ghost cell, the real
   * fixed-child cell (keyed by its `spanId`) is repositioned in place — see
   * `renderGhost`. The override is also applied in `fixedChildrenForRow` so a
   * full re-emit mid-drag keeps the bar at the dragged range. The new-event
   * create ghost still uses the separate `$ghost` cell.
   */
  #dragGhost: Option<{
    id: string;
    rowId: RowLocalId;
    range: NaiveDate.Range;
  }> = null;
  /** Canonical lane (`rowIdx`) per `spanId`, recorded by `fixedChildrenForRow`
   *  so `renderGhost` can reposition the real cell without re-laning. */
  #laneBySpanId = new Map<string, number>();
  /** The client's event categories and drafts (`sdk.events`): the cells' titles and colours. */
  readonly #presentation: EventItemPresentation.Scope;

  readonly #registerGestures: Option<(
      manager: TimelineLayoutManager<
        EventItemAllDay,
        AllDayEventGhost,
        AllDayEventInfo
      >,
      actions: TimelineRowEventRendererPlugin.Actions,
    ) => void>;
  readonly #coordinator: GestureCoordinatorLike;
  readonly #eventActiveState: EventActiveState;
  constructor(
    public readonly makeRowOptions: RowOptionsElement.Factory,
    public readonly actions: TimelineRowEventRendererPlugin.Actions,
    eventActiveState: EventActiveState,
    coordinator: GestureCoordinatorLike,
    _calendars: CalendarsClient,
    presentation: EventItemPresentation.Scope,
    registerGestures: Option<(
      manager: TimelineLayoutManager<
        EventItemAllDay,
        AllDayEventGhost,
        AllDayEventInfo
      >,
      actions: TimelineRowEventRendererPlugin.Actions,
    ) => void>,
  ) {
    this.#eventActiveState = eventActiveState;
    this.#coordinator = coordinator;
    this.#registerGestures = registerGestures;
    this.#presentation = presentation;
    // Set up column resolver for computing correct column with subindex from date
    this.actions.setColumnResolver((date, rowId) => {
      const handle = this.#handlesByRowId.get(rowId);
      if (!handle) {
        throw new Error(`No handle for row: ${rowId}`);
      }
      const mapper = handle.rebind.sheet.indexRenderer.mapper;
      const x = ColumnIndex.Key.fromDate(date, "lower");
      return mapper
        .resolveKeyToDefault(x, "lower")
        .withSubindex(mapper.indexOfKey(x).subindex);
    });

    // Set up ghost dismisser for global ghost dismissal
    this.actions.setGhostDismisser(() => this.dismissGhost());

    // Set up ghost renderer for rendering ghosts when transferring from timed mode
    this.actions.setGhostRenderer((ghost) => this.renderGhost(ghost));

    // Calendar visibility changes are handled by the invalidation sink
    // (CalendarVisibilityChanged → CellContentAll → rebindAll).
  }

  /**
   * Cleans up all resources associated with a row.
   * Should be called when a row is removed from the sheet.
   */
  cleanupRow(rowId: RowLocalId): void {
    const manager = this.#managers.get(rowId);
    if (manager) {
      manager.destroy();
      this.#managers.delete(rowId);
    }

    this.#handlesByRowId.delete(rowId);
    this.#lastAllDayBreadcrumb.delete(rowId);

    if (this.#ghostRowId === rowId) {
      this.#ghostRowId = null;
    }
  }

  /**
   * Gets or creates a TimelineLayoutManager for the given row.
   */
  #getOrCreateManager(
    row: SheetRow.Joined.Handle,
    handle: SheetDelegate,
  ): TimelineLayoutManager<
    EventItemAllDay,
    AllDayEventGhost,
    AllDayEventInfo
  > {
    let manager = this.#managers.get(row.id);
    if (!manager) {
      const keepGhostWithinVisibleRows =
        row.as(ColumnTimelineRow)?.keepGhostWithinVisibleRows ?? true;
      // Create new manager for this row
      manager = new TimelineLayoutManager<
        EventItemAllDay,
        AllDayEventGhost,
        AllDayEventInfo
      >({
        maxRows: this.config.maxRows,
        defaultVisibleRows: this.config.defaultNumSubrows,
        spanIdForItem: (item) => `ev_full_${item.id.replaceAll("-", "_")}`,
        overflowIdForRange: (dse) =>
          `ev_full_more_${NaiveDate.fromDse(dse as DaysSinceEpoch).rfc3339()}`,
        keepGhostWithinVisibleRows,
      });

      // Initialize with self as delegate; gesture registration delegated to app layer
      manager.initialize(this);
      this.#registerGestures?.(manager, this.actions);

      this.#managers.set(row.id, manager);
    }

    // Always update resolve callback with current sheet context.
    // Use the global pointer position (contentX) for cross-column resolution.
    // Resolve dates from contentX (global sheet pointer position) via the
    // scroll engine's column index lookup. This works correctly even when
    // setPointerCapture is active on a column, because contentX uses
    // e.clientX (true mouse position, unaffected by pointer capture).
    const state = handle.state;
    let lastKnownDate: Option<NaiveDate> = null;
    manager.setResolveCallback((position) => {
      const ptr = state.pointerPosition.get();
      if (ptr) {
        const date = state.dateForContentX(ptr.contentX);
        if (date) {
          lastKnownDate = date;
          return date;
        }
      }
      // Fallback: use last known date or column-local position
      if (lastKnownDate) return lastKnownDate;
      return position.date.toRange().start;
    });

    return manager;
  }

  /**
   * Per-row layout manager, when this row has bound through the event
   * renderer. `TimelineRowLayoutPlugin`'s descriptor override reads it so the
   * strip's row height tracks `effectiveVisibleRows` (chevron expansion
   * + the ghost's pending lane).
   */
  managerFor(
    rowId: RowLocalId,
  ): Option<
    TimelineLayoutManager<EventItemAllDay, AllDayEventGhost, AllDayEventInfo>
  > {
    return this.#managers.get(rowId) ?? null;
  }

  optionsInfo(_row: SheetRow.Joined.Handle): Option<string> {
    return "Timeline rows display multi-day events from the linked calendar row.";
  }

  makeCell(): TimelineColumnElement<AllDayEventInfo> {
    // Keeps the component's default weekend tint, matching the
    // `TimelineRowLayoutPlugin` band surface.
    const $c = TimelineColumnElement.make() as TimelineColumnElement<AllDayEventInfo>;

    // Register for click-outside dismissal
    this.#coordinator.registerClickOutside($c);

    return $c;
  }

  async bindCell(
    $cell: TimelineColumnElement<AllDayEventInfo>,
    context: BindCellContext,
  ) {
    this.bindStripGestureSurface($cell, context);
  }

  /**
   * Wire the all-day strip's full-width `TimelineColumnElement` surface to THIS row's
   * gesture manager via `asColumnDelegate`, so an existing-event drag started
   * on a per-bar cell keeps receiving moves once the cursor leaves the (few-px)
   * bar — mirroring how `timeline[column]`'s full-width column surface continues
   * a timed-event drag. Both the per-bar cells and this surface dispatch into
   * the same per-row `TimelineLayoutManager`.
   *
   * The strip surface binds an event-less `AllDayEventInfo` (`info.event` null):
   * `Move`/`Resize` ignore `info.event` during a move (they use stored state +
   * the global resolve callback), so strip-sourced moves continue the active
   * gesture; `Move.canHandle` requires `info.event`, so a strip pointerdown
   * won't start a spurious move. The `TimelineRowLayoutPlugin` (which owns the band's
   * main cell) calls this from its own `bindCell`/`onResize`.
   */
  bindStripGestureSurface(
    $cell: TimelineColumnElement<AllDayEventInfo>,
    context: BindCellContext,
  ): void {
    const { column, row, handle } = context;
    const [partialDate, tz] = column.discrete();
    const manager = this.#getOrCreateManager(row, handle);
    $cell.setCallbacks(manager.asColumnDelegate);
    $cell.setHoveredDate(optionLiveData(manager.hoveredDate));
    $cell.bind(
      new AllDayEventInfo(
        row.id,
        null,
        tz,
        column.withSubindex(new Rational(0, 1)),
        0,
        // The strip surface is where an all-day create starts: it carries the
        // list's access so a read-only list offers none.
        TimelineRowEventRendererPlugin.calendarListAccessOf(
          findTimelineProviderForRendererRow(row, handle.pluginManager),
        ),
      ),
      { column: partialDate, widthPx: context.widthPx },
    );
  }

  legendFactory: Option<SheetLegendClass<AllDayEventLegend>> = (() => {
    const plugin = this;
    return class {
      static make() {
        return AllDayEventLegend.make();
      }
      static bind(
        $l: AllDayEventLegend,
        row: SheetRow.Joined.Handle,
        handle: SheetDelegate,
      ) {
        // Get or create manager for this row
        const manager = plugin.#getOrCreateManager(row, handle);
        $l.bind(row, handle, manager);
      }

      static computeWidth(_row: SheetRow.Joined.Handle, measure: TextMeasurer): number {
        return AllDayEventLegendLayout.width(measure);
      }
    };
  })();

  async fixedChildrenForRow(
    row: SheetRow.Joined.Handle,
    context: FixedChildRowContext,
  ): Promise<SheetDelegate.FixedChildSpec[]> {
    const rowId = row.id;
    const handle = context.handle;
    this.#handlesByRowId.set(rowId, handle);

    const tz = await TimezoneRegion.local();
    const tzColumns = context.renderedColumns.discrete();
    const contentProvider = this.#resolveContentProvider(row, context);
    if (contentProvider?.ensureColumnsLoaded) {
      await contentProvider.ensureColumnsLoaded(tzColumns);
    }

    const range = tzColumns.toRange();
    const list = TimelineRowEventRendererPlugin.calendarListAccessOf(contentProvider);
    const events = contentProvider
      ? contentProvider.items(range)
      : await this.#getFilteredEvents(row, range, context);

    const manager = this.#getOrCreateManager(row, handle);
    const layout = manager.computeLayout(events);

    // The all-day strip's two halves diverge HERE. Geometry (the weekend-tint
    // `TimelineColumnElement` surface) is painted per column by `TimelineRowLayoutPlugin`
    // and is unconditional; CONTENT is only these fixed children, and they
    // exist solely for events the provider returned for the rendered range.
    // So an "empty strip that still shows its bands" is either `provider=false`
    // (discovery missed the calendar-list — a real bug) or `events=0` (nothing
    // all-day in this window — not a bug). Emit-on-change only: this runs on
    // every rendered-columns emit, dozens/sec during initial paint.
    const breadcrumb = {
      rowId: String(rowId),
      provider: contentProvider != null,
      events: events.length,
      painted: layout.visible.length,
      overflow: layout.overflow.size,
      from: range.start.rfc3339(),
      to: range.end.rfc3339(),
    };
    const signature = JSON.stringify(breadcrumb);
    if (this.#lastAllDayBreadcrumb.get(rowId) !== signature) {
      this.#lastAllDayBreadcrumb.set(rowId, signature);
      Tracing.log("header/allday-fixed-children", breadcrumb);
    }

    // Build specs for all visible spans
    const sanitizeForCss = (s: string) => s.replace(/[^a-zA-Z0-9_]/g, "_");
    const mapper = handle.rebind.sheet.indexRenderer.mapper;
    const spanSpecs: SheetDelegate.FixedChildSpec[] = layout.visible.map(
      ({ item, row: rowIdx }) => {
        const iid = contentProvider ? contentProvider.itemId(item) : item.id;
        const activeId = EventId.parse(iid);
        const canonicalDr = contentProvider
          ? contentProvider.dateRange(item)
          : item.time;
        // Promote-in-place: while this event is being dragged, its bar renders
        // at the dragged range (the cull layer repositions the same cell by id);
        // the canonical lane is kept (no mid-drag re-laning).
        const ghost = this.#dragGhost;
        const dragged = ghost != null && ghost.id === iid && ghost.rowId === rowId;
        const dr = dragged ? ghost.range : canonicalDr;
        const spanId = `tl_${sanitizeForCss(rowId.toString())}_${sanitizeForCss(iid)}`;
        this.#laneBySpanId.set(spanId, rowIdx);

        // Pre-compute gesture context so closures don't depend on mutable state
        const x = ColumnIndex.Key.fromDate(dr.start, "lower");
        const xr = mapper
          .resolveKeyToDefault(x, "lower")
          .withSubindex(mapper.indexOfKey(x).subindex);
        const gestureContext = new AllDayEventInfo(rowId, item, tz, xr, rowIdx, list);

        return {
          id: spanId,
          x: {
            type: "date-range",
            startDate: dr.start,
            endDate: dr.end,
          },
          inset: { left: 0, top: 4 + rowIdx * this.config.cellHeightPx },
          widthBehavior: "fill" as const,
          makeElement: () => {
            const $cell = contentProvider
              ? contentProvider.makeCell()
              : AllDayEventsCell.make();
            if (contentProvider) {
              contentProvider.bindCell($cell, item, this.#today);
            } else {
              ($cell as AllDayEventsCell).bind(item, this.#today, this.#presentation);
            }
            if (activeId != null) this.#eventActiveState.bind(activeId, $cell);
            ($cell as AllDayEventsCell).assignStyles?.({
              "--additional-width": "-2px",
            });
            ($cell as AllDayEventsCell).setCallbacks?.(
              manager.asCellDelegateWithContext(gestureContext),
            );
            return $cell;
          },
          onUpdate: ($element) => {
            if (contentProvider) {
              contentProvider.bindCell($element, item, this.#today);
            } else {
              ($element as AllDayEventsCell).bind(item, this.#today, this.#presentation);
            }
            if (activeId != null) this.#eventActiveState.bind(activeId, $element);
            ($element as AllDayEventsCell).setCallbacks?.(
              manager.asCellDelegateWithContext(gestureContext),
            );
            // The dragged bar is dimmed in place (promote-in-place) instead of
            // dimming the original while a separate ghost floats. Guarded:
            // this runs on every rendered-columns change.
            const opacity = dragged ? "0.5" : "1";
            if ($element.style.opacity !== opacity) {
              $element.style.opacity = opacity;
            }
          },
        };
      },
    );

    // Build specs for overflow indicators
    const visibleRows = manager.effectiveVisibleRows;
    const overflowSpecs: SheetDelegate.FixedChildSpec[] = [
      ...layout.overflow.entries(),
    ].map(([rangeKey, items]) => {
      const date = NaiveDate.fromDse(rangeKey as DaysSinceEpoch);
      const indicatorId = `tl_more_${sanitizeForCss(rowId.toString())}_${date.rfc3339()}`;

      const x = ColumnIndex.Key.fromDate(date, "lower");
      const xr = mapper
        .resolveKeyToDefault(x, "lower")
        .withSubindex(mapper.indexOfKey(x).subindex);
      const position = { x: xr, y: rowId };

      return {
        id: indicatorId,
        x: {
          type: "date-range",
          startDate: date,
          endDate: date,
        },
        inset: { left: 0, top: 2.5 + visibleRows * this.config.cellHeightPx },
        widthBehavior: "auto" as const,
        makeElement: () => {
          if (contentProvider?.makeOverflowIndicator) {
            const $indicator = contentProvider.makeOverflowIndicator();
            contentProvider.bindOverflowIndicator?.(
              $indicator,
              items,
              handle,
              position,
              visibleRows,
            );
            return $indicator;
          }
          const $indicator = MoreIndicator.make();
          this.#bindOverflowIndicator(
            $indicator,
            items,
            handle,
            xr,
            rowId,
            visibleRows,
          );
          return $indicator;
        },
        onUpdate: ($element) => {
          if (contentProvider?.bindOverflowIndicator) {
            contentProvider.bindOverflowIndicator(
              $element,
              items,
              handle,
              position,
              visibleRows,
            );
            return;
          }
          this.#bindOverflowIndicator(
            $element as MoreIndicator,
            items,
            handle,
            xr,
            rowId,
            visibleRows,
          );
        },
      };
    });

    return [...spanSpecs, ...overflowSpecs];
  }

  /**
   * Resolve a content provider for this row by walking descendants
   * for a `calendar-list` alias whose plugin emits a
   * `makeTimelineProvider`. The all-day `timeline[row]` carries its
   * own calendar-list child, so a single direct walk suffices.
   */
  /** The calendar-list access the strip's `AllDayEventInfo` carries for `provider`'s row. */
  static calendarListAccessOf(provider: Option<TimelineContentProvider.Provider<EventItemAllDay>>): EventAccess.List {
    // No provider renders no events; a provider without a list is unrestricted.
    return provider?.calendarListAccess?.() ?? EventAccess.UNLISTED;
  }

  #resolveContentProvider(
    row: SheetRow.Joined.Handle,
    context: FixedChildRowContext,
  ): Option<TimelineContentProvider.Provider<EventItemAllDay>> {
    return findTimelineProviderForRendererRow(
      row,
      context.handle.pluginManager,
    );
  }

  /**
   * Gets filtered full-day events for the row.
   * Tries contentProviders.calendar first; returns empty when no
   * provider is bound.
   */
  async #getFilteredEvents(
    row: SheetRow.Joined.Handle,
    range: NaiveDate.Range,
    context: FixedChildRowContext,
  ): Promise<EventItemAllDay[]> {
    // Direct content provider (post-projection): walk own children
    // for a calendar-list provider's makeTimelineProvider.
    const provider = this.#resolveContentProvider(row, context);
    if (provider) {
      return provider.items(range);
    }

    // No content provider bound — degraded state (e.g. uninstalled
    // plugin). Render an empty all-day strip rather than fanning out
    // to a centralized index lookup; events live on their producer
    // row's facade now.
    return [];
  }

  // ─────────────────────────────────────────────────────────────
  // TimelineLayoutManagerDelegate
  // ─────────────────────────────────────────────────────────────

  #bindOverflowIndicator(
    $indicator: MoreIndicator,
    items: EventItemAllDay[],
    handle: SheetDelegate,
    xr: ColumnIndex.Key.Resolved.WithSubindex,
    rowId: RowLocalId,
    visibleRows: number,
  ): void {
    $indicator.bind(
      items,
      this.#presentation,
      () =>
        handle.tooltip.showTooltip(
          "all-day-event",
          EventTooltip.instance.bind(items, this.#presentation),
          { x: xr, y: rowId },
          "bottom",
          null,
        ),
      () => handle.tooltip.hideTooltip("all-day-event"),
      visibleRows,
    );
  }

  /** The fixed-child cull-layer id for an event's bar in a row (matches the
   *  `spanId` built in `fixedChildrenForRow`). */
  #spanIdFor(rowId: RowLocalId, iid: string): string {
    const sanitize = (s: string) => s.replace(/[^a-zA-Z0-9_]/g, "_");
    return `tl_${sanitize(rowId.toString())}_${sanitize(iid)}`;
  }

  renderGhost(ghost: AllDayEventGhost): void {
    // Use ghost's rowId, not currentRowId (which may be null outside render cycle)
    let rowId = ghost.rowId;

    let handle = this.#handlesByRowId.get(rowId);
    if (!handle) {
      // A compose ghost from the timed create popover carries the calendar-body
      // row id; the all-day strip is that row's virtual sibling (same parent
      // calendar row, different preset), so resolve by matching parent.
      const parentHex = RowId.fromLocalId(rowId).asVirtual()?.parentId.toHex();
      if (parentHex != null) {
        for (const [candidateId, candidateHandle] of this.#handlesByRowId) {
          if (
            RowId.fromLocalId(candidateId).asVirtual()?.parentId.toHex() ===
              parentHex
          ) {
            rowId = candidateId;
            handle = candidateHandle;
            break;
          }
        }
      }
    }
    if (!handle) return;

    // Existing-event drag → promote-in-place: reposition the REAL bar (keyed by
    // its `spanId`) at the dragged range instead of floating a separate ghost.
    // The `dragGhost` override keeps a mid-drag full re-emit consistent.
    if (ghost.type === "existing-fullday") {
      this.#dragGhost = { id: ghost.event.id, rowId, range: ghost.newDateRange };
      const spanId = this.#spanIdFor(rowId, ghost.event.id);
      const $real = handle.fixedChild.getFixedChild(spanId);
      if ($real) {
        const lane = this.#laneBySpanId.get(spanId) ?? 0;
        handle.fixedChild.revealDates(
          spanId,
          $real,
          { x: ghost.newDateRange.start, y: rowId },
          {
            widthBehavior: "fill",
            inset: { left: 0, top: 4 + lane * this.config.cellHeightPx },
            endDate: ghost.newDateRange.end,
            animate: false,
          },
        );
        $real.style.opacity = "0.5";
      }
      // Preserve row-height expansion when the drag needs a new lane.
      const mgr = this.#managers.get(rowId);
      if (mgr?.pendingGhostRowIdx != null) {
        handle.state.sheet.invalidateLayout();
      }
      return;
    }

    // New-event create ghost → separate ghost cell (unchanged).
    if (this.#ghostRowId && this.#ghostRowId !== rowId) {
      const oldHandle = this.#handlesByRowId.get(this.#ghostRowId);
      oldHandle?.fixedChild.dismiss(GHOST_ID, "instant");
    }
    this.#ghostRowId = rowId;

    if (!this.#$ghost) {
      this.#$ghost = AllDayEventsCell.make();
      this.#$ghost.assignStyles({
        boxShadow: "var(--box-shadow-light)",
        "--additional-width": "-2px",
      });
      // The new-event ghost is part of the create flow, but it's an overlay
      // outside the create popover's subtree — so a pointer-down on it (e.g.
      // grabbing a resize handle while composing all-day from the timed popover)
      // reads as click-outside and dismisses the popover. Tag it with the
      // whitelisted create-form context so the coordinator treats it as inside.
      this.#$ghost.setAttribute("context", "create-form");
    }

    const dateRange: NaiveDate.Range = ghost.dateRange;
    this.#$ghost.bindNewEvent(
      ghost.title ?? "New Event",
      ghost.backgroundColor,
      dateRange,
    );
    const rowIdx: number = ghost.rowIdx; // New event ghost renders at assigned row

    // Ghost body has pointer-events: none, but handles have pointer-events: auto
    // to show resize cursor and forward events to the gesture system.
    this.#$ghost.style.pointerEvents = "none";

    // Keep the create-form context on the ghost even across cell reuse/recycle
    // (attributes can be cleared), so a pointer-down on it never reads as a
    // click-outside of the create popover.
    this.#$ghost.setAttribute("context", "create-form");

    // Set up callbacks for the ghost's drag handles
    // Use the timezone from the ghost (captured when the gesture started)
    const manager = this.#managers.get(rowId);
    if (manager) {
      const mapper = handle.rebind.sheet.indexRenderer.mapper;
      const x = ColumnIndex.Key.fromDate(dateRange.start, "lower");
      const xr = mapper
        .resolveKeyToDefault(x, "lower")
        .withSubindex(mapper.indexOfKey(x).subindex);
      // A create ghost exists only where the list offered creating.
      const context = new AllDayEventInfo(rowId, null, ghost.tz, xr, rowIdx, EventAccess.WRITING_LIST);
      this.#$ghost.setCallbacks(manager.asCellDelegateWithContext(context));
    }

    const ghostInsetTop = 4 + rowIdx * this.config.cellHeightPx;
    const manager2 = this.#managers.get(rowId);
    Tracing.log("allday-ghost-reveal", {
      rowIdx,
      ghostInsetTop,
      cellHeightPx: this.config.cellHeightPx,
      visibleRows: manager2?.visibleRows,
      effectiveVisibleRows: manager2?.effectiveVisibleRows,
    });

    handle.fixedChild.revealDates(
      GHOST_ID,
      this.#$ghost,
      {
        x: dateRange.start,
        y: rowId,
      },
      {
        widthBehavior: "fill",
        inset: {
          left: 0,
          top: ghostInsetTop,
        },
        endDate: dateRange.end,
        animate: false,
      },
    );

    // If ghost needs row expansion, trigger layout recalculation
    if (manager?.pendingGhostRowIdx != null) {
      handle.state.sheet.invalidateLayout();
    }
  }

  dismissGhost(): void {
    // Existing-event promote-in-place: clear the override and un-dim the real
    // bar (it's already at the dropped range via `renderGhost`). On drop the
    // persist's canonical re-render confirms; on cancel a re-render restores the
    // original range.
    if (this.#dragGhost) {
      const { id, rowId } = this.#dragGhost;
      const wasManager = this.#managers.get(rowId);
      const hadExpansion = wasManager?.pendingGhostRowIdx != null;
      wasManager?.setPendingGhostRowIdx(null);
      wasManager?.clearGhost();
      this.#dragGhost = null;
      const handle = this.#handlesByRowId.get(rowId);
      const $real = handle?.fixedChild.getFixedChild(this.#spanIdFor(rowId, id));
      if ($real) $real.style.opacity = "1";
      if (handle && hadExpansion) handle.state.sheet.invalidateLayout();
    }

    // New-event create ghost: dismiss the separate ghost cell.
    if (this.#ghostRowId) {
      const handle = this.#handlesByRowId.get(this.#ghostRowId);
      const manager = this.#managers.get(this.#ghostRowId);
      const hadExpansion = manager?.pendingGhostRowIdx != null;

      // Clear manager's ghost state
      manager?.setPendingGhostRowIdx(null);
      manager?.clearGhost();

      handle?.fixedChild.dismiss(GHOST_ID, "instant");
      this.#ghostRowId = null;
      // Note: $ghost element is kept for reuse in subsequent drag sessions

      // Trigger layout recalculation
      if (handle && hadExpansion) {
        handle.state.sheet.invalidateLayout();
      }
    }
  }

  onVisibleRowsChanged(_visibleRows: number): void {
    // Called when RowHeightCalculator determines visible rows changed
    // Only relevant during ghost drag (setPendingGhostRowIdx triggers this)
    const rowId = this.#ghostRowId;
    if (!rowId) return;

    const handle = this.#handlesByRowId.get(rowId);
    if (handle) {
      // Recalculate layout + update headerPeek height via bindScrollProps
      handle.rebind.invalidateRowHeight();
      // Re-run fixedChildrenForRow so more-indicator gets correct inset.top
      handle.rebind.rebindViewports();
    }
  }

  setGhostPointerEvents(enabled: boolean): void {
    // Track the disabled state for gesture coordination.
    // Ghost always has pointer-events: none - the TimelineColumnElement handles all pointer events.
    // This method is kept for API compatibility with gestures that call it during drag.
    this.#ghostPointerEventsDisabled = !enabled;
  }

  onResize() {}
  loadData = null;
  onBindRow() {}
  onViewportColumnsChanged() {}
  onRenderedColumnsWillChange() {}
}

/** Types that go with `TimelineRowEventRendererPlugin`: `Attributes`, `Actions`. */
export namespace TimelineRowEventRendererPlugin {
  export type Attributes = {
    linkedCalendarRowId?: RowNetworkKey;
    keepGhostWithinVisibleRows?: boolean;
  };

  export interface Actions extends AllDayActionsLike {
    setColumnResolver(
      resolver: (
        date: NaiveDate,
        rowId: RowLocalId,
      ) => ColumnIndex.Key.Resolved.WithSubindex,
    ): void;
    setGhostDismisser(dismisser: () => void): void;
    setGhostRenderer(renderer: (ghost: AllDayEventGhost) => void): void;
  }
}
